'use client';

/**
 * Lock Indicator Component
 *
 * Visual indicator for locked features with tier badge support.
 * Used by GlassButton, GlossyMenuItem, GlassTabs, UnifiedSidebar, etc.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import LockIcon from '@mui/icons-material/Lock';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, useTheme } from '@mui/material';
import type { LockState } from '@ops-matrix/shared-types';
import { SubscriptionTier } from '@ops-matrix/shared-types';
import React from 'react';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface LockIndicatorProps {
  /** Lock state containing type and reason */
  lockState: LockState;

  /** Size of the lock icon */
  size?: 'small' | 'medium' | 'large';

  /** Whether to show the tier badge (only for tier locks) */
  showTierBadge?: boolean;

  /** Whether to show tooltip with lock reason */
  showTooltip?: boolean;

  /** Custom tooltip text (overrides lockState.reason) */
  tooltipText?: string;

  /** Use outlined icon variant */
  outlined?: boolean;

  /** Additional styles */
  sx?: React.ComponentProps<typeof Box>['sx'];
}

// ============================================================================
// Constants
// ============================================================================

const SIZE_MAP = {
  small: 14,
  medium: 18,
  large: 22,
};

const TIER_COLORS: Record<SubscriptionTier, 'default' | 'primary' | 'warning' | 'error'> = {
  [SubscriptionTier.FREE]: 'default',
  [SubscriptionTier.STARTER]: 'primary',
  [SubscriptionTier.PROFESSIONAL]: 'warning',
  [SubscriptionTier.ENTERPRISE]: 'error',
};

const TIER_LABELS: Record<SubscriptionTier, string> = {
  [SubscriptionTier.FREE]: 'Free',
  [SubscriptionTier.STARTER]: 'Starter',
  [SubscriptionTier.PROFESSIONAL]: 'Pro',
  [SubscriptionTier.ENTERPRISE]: 'Enterprise',
};

// ============================================================================
// Component
// ============================================================================

/**
 * Lock Indicator
 *
 * Displays a lock icon with optional tier badge for locked features.
 *
 * @example
 * ```tsx
 * // Permission lock (grey)
 * <LockIndicator lockState={{ type: 'permission', reason: 'No access' }} />
 *
 * // Tier lock with badge
 * <LockIndicator
 *   lockState={{ type: 'tier', reason: 'Upgrade required', requiredTier: SubscriptionTier.PROFESSIONAL }}
 *   showTierBadge
 * />
 *
 * // License lock
 * <LockIndicator lockState={{ type: 'license', reason: 'NMLS required' }} />
 * ```
 */
export function LockIndicator({
  lockState,
  size = 'small',
  showTierBadge = true,
  showTooltip = true,
  tooltipText,
  outlined = false,
  sx,
}: LockIndicatorProps) {
  const theme = useTheme();
  const iconSize = SIZE_MAP[size];

  // Determine icon color based on lock type
  const getIconColor = () => {
    switch (lockState.type) {
      case 'tier':
        return theme.palette.warning.main;
      case 'license':
        return theme.palette.info.main;
      case 'permission':
      default:
        return theme.palette.action.disabled;
    }
  };

  const IconComponent = outlined ? LockOutlinedIcon : LockIcon;

  const content = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        ...sx,
      }}
    >
      <IconComponent
        sx={{
          fontSize: iconSize,
          color: getIconColor(),
        }}
      />
      {showTierBadge && lockState.type === 'tier' && lockState.requiredTier && (
        <GlassChip
          label={TIER_LABELS[lockState.requiredTier]}
          size="small"
          color={TIER_COLORS[lockState.requiredTier]}
          sx={{
            height: size === 'small' ? 16 : size === 'medium' ? 20 : 24,
            fontSize: size === 'small' ? '0.625rem' : size === 'medium' ? '0.7rem' : '0.75rem',
            '& .MuiChip-label': {
              px: 0.5,
            },
          }}
        />
      )}
    </Box>
  );

  if (!showTooltip) {
    return content;
  }

  return (
    <GlassTooltip title={tooltipText ?? lockState.reason} arrow placement="top">
      {content}
    </GlassTooltip>
  );
}

// ============================================================================
// Compact Lock Icon (for tight spaces)
// ============================================================================

export interface CompactLockIconProps {
  /** Lock type */
  type: 'permission' | 'tier' | 'license';

  /** Size in pixels */
  size?: number;

  /** Color override */
  color?: string;
}

/**
 * Compact Lock Icon
 *
 * Minimal lock icon for use in table cells, chips, etc.
 */
export function CompactLockIcon({ type, size = 14, color }: CompactLockIconProps) {
  const theme = useTheme();

  const defaultColor = (() => {
    switch (type) {
      case 'tier':
        return theme.palette.warning.main;
      case 'license':
        return theme.palette.info.main;
      case 'permission':
      default:
        return theme.palette.action.disabled;
    }
  })();

  return (
    <LockIcon
      sx={{
        fontSize: size,
        color: color ?? defaultColor,
      }}
    />
  );
}

export default LockIndicator;
